import {
  ChangeDetectionStrategy,
  Component,
  Input,
  booleanAttribute,
  computed,
  forwardRef,
  signal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
type Option = {
  label: string;
  value: string;
};
@Component({
  selector: 'app-drop-down',
  templateUrl: './drop-down.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropDownComponent),
      multi: true
    }
  ]
})
export class DropDownComponent implements ControlValueAccessor {
  @Input({ required: true }) options!: Option[];
  @Input({ transform: booleanAttribute }) multiple = false;
  @Input({ transform: booleanAttribute }) searchable = false;
  @Input({ transform: booleanAttribute }) required = false;
  @Input() placeholder = '';
  @Input({ required: true }) header = '';
  @Input({ required: true }) label = '';

  private readonly query = signal('');
  private readonly _selected = signal('');
  private readonly _display = signal('');
  private readonly _isActive = signal(false);
  private readonly _isDisabled = signal(false);
  protected readonly selected = this._selected.asReadonly();
  protected readonly display = this._display.asReadonly();
  protected readonly isActive = this._isActive.asReadonly();
  protected readonly isDisabled = this._isDisabled.asReadonly();
  protected readonly filteredOptions = computed(() =>
    this.options.filter(option => option.label.toLowerCase().includes(this.query().toLowerCase()))
  );
  protected readonly updateQuery = (e: Event) => {
    const value = (e.target as HTMLInputElement).value;
    this.query.set(value);
  };
  protected readonly toggleActive = () => {
    this._isActive.update(v => !v);
  };
  protected readonly select = (option: Option) => {
    this.query.set('');
    if (!this.multiple) this.toggleActive();
    if (this.multiple && this.selected()) {
      this.selectMultiple(option);
      return;
    }
    this._display.set(option.label);
    this._selected.set(option.value);
    this.onChange(option.value);
  };
  protected readonly closeDropdown = () => {
    if (!this.isActive()) return;
    this.onTouch(true);
    this._isActive.set(false);
  };
  private readonly selectMultiple = (option: Option) => {
    const selectedValues = this.selected().split(',');
    const { value } = option;
    let result!: string;
    if (selectedValues.includes(value))
      result = selectedValues.filter(_value => _value !== value).join(',');
    else result = [...selectedValues, value].join(',');
    if (result.length === 0) return;
    this._selected.set(result);
    const values = result.split(',');
    const display = this.options
      .filter(option => values.includes(option.value))
      .map(option => option.label)
      .join(',');
    this._display.set(display);
    this.onChange(result);
  };
  isSelected = (option: Option) => {
    if (!this.multiple) return option.value === this.selected();
    const selectedValues = this.selected().split(',');
    return selectedValues.includes(option.value);
  };
  private onChange = (input: string) => {};
  protected onTouch = (isTouched: boolean) => {};
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this._isDisabled.set(isDisabled);
  }
  writeValue(value: string): void {
    this._selected.set(value);
    const found = this.options.find(option => option.value === value);
    if (!found) return;
    this._display.set(found.label);
    this._selected.set(found.value);
  }
  ngOnChanges() {
    this.writeValue(this.selected());
  }
}
